import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { enqueueSnackbar } from 'notistack';
import cx from 'classnames';

import { getAllChildOrganizations, getFolderCopyStatus, getGroupsWithOrganizations, patchUpdateOrgCollaborators } from '../../../api';
import { COLLABORATIVE_FOLDER_TYPE, GROUP_WITH_SUBGROUP, ORGANIZATION } from '../../../enums';
import { ReactComponent as SubgroupIcon } from '../../../assets/icons/folder-open.svg';
import { ContainedButton, OutlinedButton } from '../../../components/modules/Buttons';
import { updateTriggerFolderFetch } from '../../../store/folders/foldersSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import styles from './ShareFolderToChildModal.module.scss';
import Search from '../../../components/modules/Search';
import Avatar from '../../../components/modules/Avatar';
import Modal from '../../../components/modules/Modal';
import { NOTIFICATION } from '../../../enums/ENUMS';
import { DateFormatter } from '../../../helpers';
import { Tooltip } from '@material-ui/core';

type TAddCollaborateFolderModal = {
  open: boolean;
  setOpen: (bool: boolean) => void;
  folderId: string;
  folder: COLLABORATIVE_FOLDER_TYPE;
};

const ShareFolderToChildModal: React.FC<TAddCollaborateFolderModal> = ({ open, setOpen, folderId, folder }) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const notification = useSnackbar();
  const switchModal = () => {
    setOpen(!open);
  };
  const { myOrganization, currentUser } = useAppSelector((state) => state.user);
  const { socket } = useAppSelector((state) => state.sharedStore);

  const [search, setSearch] = useState('');

  const [selectedOrgs, setSelectedOrgs] = useState<number[]>([]);

  useEffect(() => {
    if (open && folder?.organization_collaborators?.length) {
      setSelectedOrgs(folder.organization_collaborators.map((org) => +org.id));
    }
    if (open && folder?.group_collaborators?.length) {
      setSelectedGroups(folder.group_collaborators.map((g) => +g.id));
    }
  }, [open, folder]);

  const [childOrgs, setChildOrgs] = useState<ORGANIZATION[]>([]);
  const [groupOrgs, setGroupOrgs] = useState<GROUP_WITH_SUBGROUP[]>([]);
  const onClose = () => {
    switchModal();
    setChildOrgs([]);
    setSearch('');
    setAlreadyDone(false);
    setIsCopying(false);
  };

  useEffect(() => {
    console.log('folder', folder);
  }, [folder]);

  const [isCopying, setIsCopying] = useState(false);

  const fetchOrgs = async () => {
    try {
      setLoading(true);
      const res = await getAllChildOrganizations({ id: +myOrganization!.id, page: 0, limit: 1000, search: '' });

      const resOrgGroups = await getGroupsWithOrganizations('', 0, 100);
      if (resOrgGroups?.documents) {
        setGroupOrgs(resOrgGroups.documents);
      }

      const resData = await getFolderCopyStatus(folderId);
      if (typeof resData?.data?.isCopying === 'boolean') {
        setIsCopying(resData?.data?.isCopying);
      }
      setChildOrgs(res.data.documents);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && folderId) {
      fetchOrgs();
    }
  }, [open, folderId]);

  const [alreadyDone, setAlreadyDone] = useState(false);
  const submitNewFolder = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (alreadyDone) {
      onClose();
      return;
    }
    setLoading(true);
    try {
      setProgress([]);
      setIsCopying(true);
      await patchUpdateOrgCollaborators(
        {
          orgCollaborators: selectedOrgs,
          groupIds: selectedGroups,
        },
        +folderId!,
      );
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage(NOTIFICATION.SOMETHING_WENT_WRONG);
      }
    }
  };

  const [progress, setProgress] = useState<
    {
      message: string;
      id: number;
      status: string;
    }[]
  >([]);

  useEffect(() => {
    if (currentUser && socket?.id && open) {
      const handler = (msg: string) => {
        /*
          msg: {
            type: 'storage',
            event: 'changed',
            message: Storage usage changed,
            data: '{ storage_usage: organization.storage_usage }',
          }
          as string
        */

        const msgObj = JSON.parse(msg);
        if (+msgObj?.parent_folder_id === +folderId) {
          setLoading(true);
          if (msgObj.data) {
            const parsed = JSON.parse(msgObj.data);
            // pick whichever id is present
            const id = parsed.videofeed_id ?? parsed.folder_id;
            const status = parsed.status;
            setProgress((prev) => {
              // skip if we've already seen this id+status
              if (prev.some((p) => p.id === id && p.status === status)) {
                return prev;
              }
              return [...prev, { message: `${msgObj.message} ${DateFormatter('', true, true)}`, id, status }];
            });
          } else if (msgObj.message) {
            const datedMessage = `${msgObj.message} ${DateFormatter('', true, true)}`;
            if (msgObj.message.includes('Distribution is Finished')) {
              dispatch(updateTriggerFolderFetch());
              setLoading(false);
              enqueueSnackbar(`Successfully Updated folder: ${folder.name} `, {
                variant: 'success',
                anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
                persist: true,
              });
              setAlreadyDone(true);
              setIsCopying(false);
            }
            setProgress((prev) => [...prev, { ...msgObj, message: datedMessage }]);
          }
        }
      };

      socket.on('distribution', handler);
      return () => void socket.off('distribution', handler);
    }
  }, [socket?.id, open]);

  const onOrgSelect = (id: number) => {
    if (selectedOrgs.find((orgId) => +orgId === +id)) {
      const arr = selectedOrgs.filter((orgId) => +orgId !== +id);
      setSelectedOrgs(arr);
    } else {
      setSelectedOrgs([...selectedOrgs, id]);
    }
  };

  const [selectedGroups, setSelectedGroups] = useState<number[]>(folder.group_collaborators.map((g) => +g.id));
  const onGroupSelect = (id: number) => {
    if (selectedGroups.find((orgId) => +orgId === +id)) {
      const arr = selectedGroups.filter((orgId) => +orgId !== +id);
      setSelectedGroups(arr);
    } else {
      setSelectedGroups([...selectedGroups, id]);
    }
  };

  return (
    <Modal open={open} onClose={loading || isCopying ? console.log : onClose} loading={loading || isCopying}>
      <div className={styles.modalContainer}>
        <div className={styles.modalTitle}>Child organisations </div>
        <div className={styles.folderName}>{folder.name}</div>
        <form onSubmit={submitNewFolder} className={styles.form}>
          <Search onChange={(e) => setSearch(e.target.value)} className={cx(styles.searchField, !search ? styles.transparentSearch : '')} />
          {childOrgs.length > 0 && (
            <div className={cx(styles.childOrgsList, alreadyDone ? styles.filter : '')}>
              {childOrgs
                .filter(
                  (o) =>
                    o.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()) ||
                    o.location.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
                )
                .map((org) => (
                  <div className={styles.user} key={org.id}>
                    <Avatar avatar={org.image_url} name={org.name} />{' '}
                    <div className={styles.info}>
                      <div className={styles.username}>{org.name}</div>
                      <div className={styles.email}>{org.location} </div>
                    </div>
                    <div className={styles.checkboxWrapper30}>
                      <span className={styles.checkbox}>
                        <input
                          type="checkbox"
                          onChange={() => onOrgSelect(+org.id)}
                          checked={!!selectedOrgs.find((orgId) => +orgId === +org.id)}
                          disabled={loading || alreadyDone}
                        />
                        <svg>
                          <use xlinkHref="#checkbox-30" className={styles.checkbox}></use>
                        </svg>
                      </span>
                      <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                        <symbol id="checkbox-30" viewBox="0 0 22 22">
                          <path
                            fill="none"
                            stroke="currentColor"
                            d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                          />
                        </symbol>
                      </svg>
                    </div>
                  </div>
                ))}
            </div>
          )}

          <div className={styles.label}>Groups:</div>
          {groupOrgs.length > 0 && (
            <div className={cx(styles.childOrgsList, alreadyDone ? styles.filter : '')}>
              {groupOrgs.map((group) => (
                <div className={styles.user} key={group.id}>
                  <div className={styles.info}>
                    <div className={styles.username}>
                      <SubgroupIcon /> {group.title}
                    </div>
                  </div>

                  <div className={styles.checkboxWrapper30}>
                    <Tooltip
                      placement="right"
                      arrow
                      title={
                        <div>
                          {' '}
                          {group.organizations.map((org) => (
                            <div key={org.name} style={{ marginBottom: '8px' }}>
                              {' '}
                              <span style={{ fontSize: '12px', fontWeight: 500 }}> {org.name} </span> <br />{' '}
                              <span style={{ filter: 'opacity(0.8)' }}> {org.location} </span>{' '}
                            </div>
                          ))}{' '}
                        </div>
                      }
                    >
                      <div className={styles.childOrgs}>
                        <SubgroupIcon /> {group.organizations.length}{' '}
                      </div>
                    </Tooltip>
                    <span className={styles.checkbox}>
                      <input
                        type="checkbox"
                        onChange={() => onGroupSelect(+group.id)}
                        checked={!!selectedGroups.find((groupId) => +groupId === +group.id)}
                        disabled={loading || alreadyDone}
                      />
                      <svg>
                        <use xlinkHref="#checkbox-30" className={styles.checkbox}></use>
                      </svg>
                    </span>
                    <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                      <symbol id="checkbox-30" viewBox="0 0 22 22">
                        <path
                          fill="none"
                          stroke="currentColor"
                          d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                        />
                      </symbol>
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          )}
          {isCopying && <div className={styles.progress}>Copying In Progress...</div>}
          {progress.length > 0 && (
            <div className={styles.progress}>
              {[...progress].reverse().map((p, i) => (
                <div key={`${p.message}_${i}`}>{p.message}</div>
              ))}
            </div>
          )}
          <ContainedButton
            kind={loading || isCopying ? 'default' : 'purple'}
            disabled={loading || isCopying}
            className={styles.customBtn}
            type="submit"
          >
            {alreadyDone ? 'Done' : 'Save changes'}
          </ContainedButton>
          <OutlinedButton kind="default" disabled={loading || alreadyDone || isCopying} onClick={onClose} type="button">
            Cancel
          </OutlinedButton>
        </form>
      </div>
    </Modal>
  );
};

export default ShareFolderToChildModal;
